// src/components/support/forms/Transport.js
import React, { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import moment from "moment";
import { useAtom } from "jotai";
import { useRequestProcessor } from "../../../requestProcessor";

import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker, { DEFAULT_FORMAT } from "../../common/FootprintDatePicker";
import { handleChangeTransport } from "../../../queryfunctions/queryfunctions";
import { childList, requestType } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../common/CustomSelect";

const Transport = () => {
    const navigate = useNavigate();
    const { useMutationWrapper } = useRequestProcessor();
    const [children] = useAtom(childList);
    const [request] = useAtom(requestType);

    const firstNonPausedIndex = children.findIndex(c => c?.pause_state !== 1);
    const [selectedChild, setSelectedChild] = useState(firstNonPausedIndex >= 0 ? firstNonPausedIndex : 0);
    const [action, setAction] = useState("");
    const [effectiveDate, setEffectiveDate] = useState();
    const [error, setError] = useState("");

    const [availableActions, setAvailableActions] = useState([]);


    console.log("request", requestType)
    // Determine available actions dynamically based on current transport status
    useEffect(() => {
        if (!request?.service_status) return;


        const { Transport1, Transport2 } = request.service_status;
        const actions = [];

        // If one-way active → show options to stop one-way and start two-way
        if (Transport1 === "Stop Transport 1 Way") {
            actions.push({ value: "stop1", label: "Stop Transport 1 Way" });
            actions.push({ value: "start2", label: "Start Transport 2 Way" });
        }

        // If two-way active → show options to stop two-way and start one-way
        else if (Transport2 === "Stop Transport 2 Way") {
            actions.push({ value: "stop2", label: "Stop Transport 2 Way" });
            actions.push({ value: "start1", label: "Start Transport 1 Way" });
        }

        // If none active → allow both to start
        else {
            actions.push({ value: "start1", label: "Start Transport 1 Way" });
            actions.push({ value: "start2", label: "Start Transport 2 Way" });
        }

        setAvailableActions(actions);
    }, [request]);

    const changeTransport = useMutationWrapper(
        ["changeTransport", children[selectedChild]?.id],
        (oneWay) =>
            handleChangeTransport(
                oneWay,
                effectiveDate,
                request,
                children[selectedChild]
            ),
        {
            onSuccess: (data) => {
                console.log("[Transport] Success:", data);
                // onBack()
            },
        }
    );

    const validate = () => {
        if (!action) {
            setError("Please select an action");
            return false;
        }
        if (!effectiveDate) {
            setError("Please select effective date");
            return false;
        }

        return true;
    };

    const submit = () => {
        setError("");
        if (!validate()) return;

        let oneWay;
        if (action === "start1" || action === "stop1") {
            oneWay = 1;
        } else if (action === "start2" || action === "stop2") {
            oneWay = 2;
        } else {
            setError("Invalid action selected");
            return;
        }

        changeTransport.mutate(oneWay);
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {changeTransport.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Transport Service</h2>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
                {/* Child selector */}
                {children.filter(c => c?.pause_state !== 1).length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            label="Select Child"
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children
                                .map((child, idx) => ({ value: idx, label: `${child?.name} ${child?.last_name}` }))
                                .filter(opt => children[opt.value]?.pause_state !== 1)
                            }
                        />
                    </div>
                )}

                {/* Action */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Action
                    </label>
                    <CustomSelect
                        value={action}
                        onChange={(val) => setAction(val)}
                        options={availableActions}
                        placeholder="Select Action"
                    />
                </div>

                {/* Effective Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Effective From
                    </label>
                    <FootprintDatePicker
                        minimumDate={moment()}
                        placeholder="Select Effective Date"
                        selected={
                            effectiveDate
                                ? moment(effectiveDate, DEFAULT_FORMAT).toDate()
                                : null
                        }
                        onChange={(date) =>
                            setEffectiveDate(moment(date).format(DEFAULT_FORMAT))
                        }
                    />
                </div>

                {/* Pickup Address */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pick-up Address
                    </label>
                    <textarea
                        placeholder="Enter your address..."
                        value={pickupAddress}
                        onChange={(e) => setPickupAddress(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 h-24 outline-none focus:border-transparent"
                    />
                </div> */}

                {/* Error */}
                {error && (
                    <div className="text-red-500 text-sm text-center">{error}</div>
                )}

                {/* Submit */}
                <button
                    onClick={submit}
                    disabled={changeTransport.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition"
                >
                    {changeTransport.isLoading ? "Submitting..." : "Submit Request"}
                </button>
            </div>
        </div>
    );
};

export default Transport;
